import { useMutation, useQueryClient } from '@tanstack/react-query'

import { createApiCall } from 'services'

type DeleteCertificateGroupParams = {
  /** The group's tagless parent certificate. */
  certificateId: string
  /** Optional subset of children to ALSO hard-delete instead of surviving as
   *  standalone certificates (see DeleteCertificateModal.tsx). */
  childIdsToDelete?: string[]
}

type DeleteCertificateGroupResult = {
  /** Certificate ids that were hard-deleted (always includes the parent). */
  deletedIds: string[]
  /** Children unlinked and surviving standalone (always empty for indivisible). */
  splitIds: string[]
}

type UseCertificateGroupDeleteOptions = {
  onSuccess?: (data: DeleteCertificateGroupResult) => void
  onError?: (error?: unknown) => void
}

// VRG-231 review round 2 item 9 — deletes a certificate group's tagless PARENT
// via the dedicated `DELETE /certificates/admin/groups/:certificateId` endpoint
// (apps/api/.../delete-certificate-group — built concurrently alongside this UI
// in the same review). Distinct from the plain single-certificate delete
// (`useCertificateDeleteMutations`, `DELETE /certificates/admin/:certificateId`),
// which has no concept of a group and would 400 against a group parent.
export const useCertificateGroupDelete = (options?: UseCertificateGroupDeleteOptions) => {
  const queryClient = useQueryClient()

  const deleteGroupMutation = useMutation({
    mutationFn: async ({ certificateId, childIdsToDelete }: DeleteCertificateGroupParams) => {
      const response = await createApiCall({
        method: 'DELETE',
        url: `/certificates/admin/groups/${certificateId}`,
        data: childIdsToDelete?.length ? { childIdsToDelete } : undefined,
      })
      return response.data as DeleteCertificateGroupResult
    },
    onSuccess: (data) => {
      // The parent, any hard-deleted children, and any split-out survivors all
      // change status — invalidate the whole certificates list/detail tree
      // rather than trying to enumerate every affected id.
      queryClient.invalidateQueries({ queryKey: ['certificates'] })
      options?.onSuccess?.(data)
    },
    onError: (error) => {
      options?.onError?.(error)
    },
  })

  return {
    deleteCertificateGroup: (params: DeleteCertificateGroupParams) => deleteGroupMutation.mutate(params),
    isDeletingGroup: deleteGroupMutation.isPending,
  }
}
